'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Send, CheckCircle, Sparkles, Code, Palette, BarChart, MessageSquare, ArrowRight } from 'lucide-react';

const departments = [
  {
    icon: <Code className="w-6 h-6" />,
    title: 'Engineering',
    description: 'Build scalable solutions that empower service professionals',
    skills: ['React', 'Node.js', 'TypeScript', 'AWS']
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: 'Design',
    description: 'Craft beautiful and intuitive user experiences',
    skills: ['UI/UX', 'Figma', 'Motion Design', 'Design Systems']
  },
  {
    icon: <BarChart className="w-6 h-6" />,
    title: 'Product',
    description: 'Shape the future of financial technology',
    skills: ['Strategy', 'Analytics', 'User Research', 'Roadmapping']
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: 'Customer Success',
    description: 'Help our users achieve their financial goals',
    skills: ['Support', 'Training', 'Documentation', 'Community']
  }
];

export function TalentPool() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | null }>({
    text: '',
    type: null,
  });

  // Auto-hide message after 5 seconds
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (message.text) {
      timeoutId = setTimeout(() => {
        setMessage({ text: '', type: null });
      }, 5000);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [message.text]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset previous messages
    setMessage({ text: '', type: null });
    
    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage({ text: 'Please enter a valid email address', type: 'error' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          source: 'talent-pool' // Tag this as talent pool signup
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ 
          text: data.message || 'Successfully joined our talent pool!', 
          type: 'success' 
        });
        setEmail(''); // Clear the input on success
      } else {
        setMessage({ 
          text: data.message || 'Failed to join talent pool. Please try again.', 
          type: 'error' 
        });
      }
    } catch (error) {
      setMessage({ 
        text: 'An error occurred. Please try again later.', 
        type: 'error' 
      });
      console.error('Talent pool subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-32 overflow-hidden bg-background">
      {/* Premium Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Base grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(45deg,theme(colors.primary.DEFAULT/0.02)_1px,transparent_1px),linear-gradient(to_right,theme(colors.primary.DEFAULT/0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
        
        {/* Gradient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[100px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-primary/5 blur-[80px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.12, 0.08, 0.12],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="text-center max-w-3xl mx-auto mb-20">
          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-block"
          >
            <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary/10 text-primary border border-primary/20 shadow-lg shadow-primary/10">
              <Star className="w-4 h-4" />
              <span className="text-sm font-medium">Join Our Talent Pool</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-8 text-4xl font-bold text-black"
          >
            Don't See the Perfect Role?{' '}
            <span className="inline-block bg-gradient-to-r from-black via-primary to-black bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
              Let's Connect
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-6 text-xl text-muted-foreground"
          >
            Join our talent pool and be the first to know about new opportunities that match your skills and interests.
          </motion.p>
        </div>

        {/* Departments Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20"
        >
          {departments.map((dept, index) => (
            <motion.div
              key={dept.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              viewport={{ once: true }}
              className="group relative"
            >
              {/* Hover Glow Effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-primary-light rounded-2xl opacity-0 group-hover:opacity-20 blur transition-all duration-300" />

              {/* Card */}
              <div className="relative p-8 rounded-2xl bg-card backdrop-blur-sm border border-primary/10 hover:border-primary/30 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
                    {dept.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                    {dept.title}
                  </h3>
                </div>
                <p className="text-muted-foreground mb-6">{dept.description}</p>
                <div className="flex flex-wrap gap-2">
                  {dept.skills.map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 text-sm rounded-full bg-primary/5 text-primary border border-primary/10"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Application Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto"
        >
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl border-2 border-input 
                         focus:border-primary focus:ring-2 focus:ring-primary/20 
                         outline-none transition-all duration-300"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-4 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-primary text-foreground hover:shadow-lg hover:shadow-primary/20'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-gray-600/30 border-t-gray-600 rounded-full"
                    />
                    <span>Joining...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Join Pool</span>
                  </>
                )}
              </button>
            </div>

            {/* Response Message */}
            <div aria-live="polite" aria-atomic="true">
              <AnimatePresence>
                {message.text && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    role={message.type === 'error' ? 'alert' : 'status'}
                    className={`mt-4 p-4 rounded-xl ${
                      message.type === 'success' 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {message.text}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              viewport={{ once: true }}
              className="mt-4 flex items-center justify-center gap-8 text-sm text-muted-foreground/80"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Top Opportunities
              </span>
              <span className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-primary" />
                Fast Response
              </span>
            </motion.div>
          </form>
        </motion.div>
      </div>
    </section>
  );
} 